/**
 * Off-screen pre-warm mount for a playlist's on-deck dashboard (S13). Mounts
 * the SAME `useTileResult` observers every visible tile view uses — the S3
 * single-fetch-path invariant — so the on-deck dashboard's tiles already
 * have fresh TanStack cache entries by the time the playlist switches to
 * them ("never appears empty then bursts"). `display:none` only affects
 * rendering — the component tree stays mounted, so hooks/effects (and
 * TanStack's observer registration) keep running normally, which is what
 * lets the S3 scheduler's eligibility-driven catch-up (`refetchTile` →
 * `queryClient.refetchQueries`) actually refresh a dashboard the user
 * visited earlier in the session.
 *
 * Param selections are hydrated first so the computed query key matches
 * exactly what the visible grid will use once this dashboard is shown.
 */
import { useEffect } from 'react';
import { useTileResult } from '../hooks/useTileResult';
import { useDashboardParamStore } from '../store/dashboardParamStore';
import { isQueryBackedTile, type DashboardTile } from '../types/dashboardTypes';
import type { QueryBackedTile } from '../services/tileExecutionService';

function PrewarmTileProbe({ dashboardId, tile }: { dashboardId: string; tile: QueryBackedTile }) {
  useTileResult(dashboardId, tile);
  return null;
}

export function DashboardPrewarmMount({
  dashboardId,
  tiles,
}: {
  dashboardId: string;
  tiles: DashboardTile[];
}) {
  useEffect(() => {
    void useDashboardParamStore.getState().hydrate(dashboardId);
  }, [dashboardId]);

  return (
    <div
      className="hidden"
      aria-hidden="true"
      data-testid="dashboard-prewarm-mount"
      data-dashboard-id={dashboardId}
    >
      {tiles.filter(isQueryBackedTile).map((tile) => (
        <PrewarmTileProbe key={tile.id} dashboardId={dashboardId} tile={tile} />
      ))}
    </div>
  );
}
